/**
 * Canonical subcategory display order for Stocktake Session inputs and outputs.
 *
 * Authoritative sequence as confirmed by user (Oct 2026).
 * This constant is the single source of truth; import it wherever
 * subcategories need to be ordered (count screen, closing stock,
 * bar prices, deliveries, multi-location report, exports).
 *
 * Mapping notes vs. actual DB subcategory values:
 *   Requested "Draught Beer/Cider"          → 'Draught'
 *   Requested "Bottle Beer/Cider"           → 'Bottled Beer & Cider'
 *   Requested "Vodka/Gin"                   → 'Vodka', 'Gin' (adjacent)
 *   Requested "Whisky/Brandy/Bourbon/Rums"  → 'Whisky / Brandy / Bourbon', 'Rum' (adjacent)
 *   Requested "Malts"                       → 'Malt Whisky'
 *   Requested "Sherry/Vermouth/Port"        → 'Fortified / Vermouth'
 *   Requested "Liqueurs"                    → 'Liqueurs'
 *   Requested "Soft Drinks"                 → 'Soft Drinks'
 *   Requested "Cordials/Post Mix"           → 'Postmix'
 *   Requested "Grab and Go"                 → "Grab 'N Go"
 *   Requested "Crisps/Nuts/Snacks"          → NO matching bucket — kept absent; report at tail if added
 *   Requested "White Wines"                 → 'White Wine'
 *   Requested "Rose Wines"                  → 'Rosé Wine'
 *   Requested "Red Wines"                   → 'Red Wine'
 *   Requested "Champagne/Sparkling"         → 'Sparkling / Champagne'
 *   Requested "Cocktail Ingredients"        → 'Cocktail Ingredients'
 *   Not in requested list (tail):           'Aperitifs / Digestifs', 'Tequila'
 */
export const SUBCATEGORY_ORDER = [
  'Draught',
  'Bottled Beer & Cider',
  'Vodka',
  'Gin',
  'Whisky / Brandy / Bourbon',
  'Rum',
  'Malt Whisky',
  'Fortified / Vermouth',
  'Liqueurs',
  'Soft Drinks',
  'Postmix',
  "Grab 'N Go",
  'White Wine',
  'Rosé Wine',
  'Red Wine',
  'Sparkling / Champagne',
  'Cocktail Ingredients',
];

/**
 * Returns the canonical position for a subcategory name.
 * Case-insensitive lookup; unknown subcategories return a high number
 * so they appear after all known buckets (tail) and sort alphabetically
 * among themselves. 'Other' (no subcategory) is always last.
 */
function _position(name) {
  if (!name || name === 'Other') return Infinity;
  const idx = SUBCATEGORY_ORDER.findIndex(
    s => s.toLowerCase() === name.toLowerCase()
  );
  return idx === -1 ? SUBCATEGORY_ORDER.length + 1 : idx;
}

/**
 * Comparator for subcategory strings — drop-in for Array.sort().
 *
 * Usage:
 *   keys.sort(subcategoryComparator)
 *   entries.sort(([a], [b]) => subcategoryComparator(a, b))
 */
export function subcategoryComparator(a, b) {
  const pa = _position(a);
  const pb = _position(b);
  if (pa !== pb) return pa - pb;
  // Same canonical position (both unknown or truly identical): alpha within tail
  if (a === 'Other') return 1;
  if (b === 'Other') return -1;
  return (a || '').localeCompare(b || '');
}
